import { useEffect, useMemo } from "react";

import { QueryErrorResetBoundary } from "@tanstack/react-query";
import type { FallbackProps } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";

import { MESSAGE_SCROLL_PADDING_Y } from "app/pages/sales-intelligence/modules/ai-agent/components/styles";
import { useChatScrollEffects } from "app/pages/sales-intelligence/modules/ai-agent/hooks/useChatScrollEffects";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { selectIsInitialChatState } from "app/pages/sales-intelligence/modules/ai-agent/store";
import {
    AIAgentChatErrorView,
    AIAgentChat,
    type IFiltersChatProps,
} from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/components/AIAgentChat";
import { useLeadGeneratorChatMessages } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/hooks";
import { setFiltersStateChangeMessage } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/store";
import type { MdComponentProps } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/types/mdComponentProps";
import type {
    LeadsSearchAllFiltersTypes,
    LeadsSearchFilterValue,
} from "app/pages/sales-intelligence/modules/leads-search-filters";
import { filterTrackingRegistry } from "app/pages/sales-intelligence/modules/leads-search-filters/services/tracking/formatters";

import ErrorBoundaryWithLogging from "../../../../common-components/ErrorBoundaryWithLogging/ErrorBoundaryWithLogging";

const MESSAGE_SCROLL_CONTAINER_BOTTOM_OFFSET = MESSAGE_SCROLL_PADDING_Y * 4;

const FiltersChatContainer = ({ filtersManager }: IFiltersChatProps<LeadsSearchAllFiltersTypes>) => {
    const dispatch = useDispatch();

    const isInitialChatState = useSelector(selectIsInitialChatState);

    const { isPending, isLoading, messages, agentMessageId, sendMessage } = useLeadGeneratorChatMessages({
        filtersManager,
        onMessageReceive(messageData) {
            AIAgentTrackingService.leadGenerator.trackAgentMessageFiltersResponse(messageData);
        },
    });

    const chatScrollEffects = useChatScrollEffects({
        sendMessage,
        scrollBottomOffset: MESSAGE_SCROLL_CONTAINER_BOTTOM_OFFSET,
    });

    useEffect(() => {
        if (agentMessageId && !isLoading) {
            chatScrollEffects.onResponseComplete();
        }
    }, [agentMessageId, isLoading, chatScrollEffects.onResponseComplete]);

    const mdComponentProps: MdComponentProps<LeadsSearchAllFiltersTypes> = useMemo(
        () => ({
            filtersManager,
            onFilterUpdate(...params) {
                filtersManager.normalizeAndUpdateFilter(...params);

                void dispatch(setFiltersStateChangeMessage());
            },
            onFilterValueRemove(key, newValue, prevValue) {
                const filterConfig = filtersManager.getFilterConfig(key);
                if (!filterConfig) {
                    return;
                }

                const valueDelta = filterConfig.filter.calculateValueDelta(prevValue, newValue);
                if (valueDelta.deleted.length === 0) {
                    return;
                }

                const valueForTracking = filterTrackingRegistry.format(
                    key,
                    valueDelta.deleted[0] as LeadsSearchFilterValue,
                );

                AIAgentTrackingService.leadGenerator.trackRemovedFilterValue(key, valueForTracking);
            },
            onMessageSubmit: chatScrollEffects.onMessageSubmit,
        }),
        [filtersManager, chatScrollEffects.onMessageSubmit, dispatch],
    );

    const withInitialInputPlaceholder = isPending || (isLoading && messages.length === 0) || isInitialChatState;

    return (
        <AIAgentChat
            isLoading={isLoading}
            isPending={isPending}
            messages={messages}
            mdComponentProps={mdComponentProps}
            withInitialInputPlaceholder={withInitialInputPlaceholder}
            containerRef={chatScrollEffects.containerRef}
            bottomRef={chatScrollEffects.bottomRef}
            lastUserMessageRef={chatScrollEffects.lastUserMessageRef}
            lastAiMessageRef={chatScrollEffects.lastAiMessageRef}
            onMessageSubmit={chatScrollEffects.onMessageSubmit}
        />
    );
};

const FallbackRenderer = ({ resetErrorBoundary }: FallbackProps) => (
    <AIAgentChatErrorView onRefresh={resetErrorBoundary} />
);

const FiltersChatContainerWithErrorBoundary = (props: IFiltersChatProps<LeadsSearchAllFiltersTypes>) => {
    return (
        <QueryErrorResetBoundary>
            {({ reset }) => (
                <ErrorBoundaryWithLogging
                    fallbackRender={FallbackRenderer}
                    onReset={reset}
                >
                    <FiltersChatContainer {...props} />
                </ErrorBoundaryWithLogging>
            )}
        </QueryErrorResetBoundary>
    );
};

export default FiltersChatContainerWithErrorBoundary;
